import { Note, ProblemBox, TextAreaField } from '@hive/ui';
import { useState } from 'react';
import { useI18nP } from '../../i18n';
import { memoryApi } from '../../lib/memory';
import { useSession } from '../../lib/session';

/**
 * Avaliação da resposta do "Perguntar à IA" (R01-16): útil / não útil e, se quiser, a correção. Útil ou corrigida vira
 * exemplo depois que quem gere a memória da IA aprova; o exemplo ensina o jeito de responder, e os números continuam
 * vindo das consultas do Hive.
 */
export function AnswerFeedback({ question, answer, source, intent, runId }: { question: string; answer: string; source: 'hive' | 'model'; intent?: string | null; runId?: string | null }) {
  const { t, lang } = useI18nP();
  const s = useSession();
  const [fixing, setFixing] = useState(false);
  const [correction, setCorrection] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const send = async (rating: 'useful' | 'not-useful', fix?: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await memoryApi.feedback(s.api, {
        question: question.slice(0, 1000),
        answer: answer.slice(0, 8000),
        source,
        rating,
        ...(intent ? { intent } : {}),
        ...(runId ? { runId } : {}),
        ...(fix?.trim() ? { correction: fix.trim().slice(0, 8000) } : {}),
        lang: lang === 'en' || lang === 'es' ? lang : 'pt-BR',
      });
      setDone(r.state ?? 'rated');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <Note tone="ok">
        <span data-testid="feedback-done">{done === 'approved' ? t('mem.fb.approved') : done === 'pending' ? t('mem.fb.pending') : t('mem.fb.rated')}</span>
      </Note>
    );
  }
  return (
    <div className="stack-sm" data-testid="answer-feedback">
      <div className="row" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span className="xs muted">{t('mem.fb.ask')}</span>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void send('useful')}>
          {t('mem.fb.useful')}
        </button>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void send('not-useful')}>
          {t('mem.fb.notUseful')}
        </button>
        <button type="button" className="btn btn-sm btn-ghost" disabled={busy} aria-expanded={fixing} onClick={() => setFixing((x) => !x)}>
          {t('mem.fb.correct')}
        </button>
      </div>
      {fixing && (
        <div className="stack-sm">
          <TextAreaField label={t('mem.fb.correctionLabel')} hint={t('mem.fb.correctionHint')} value={correction} onChange={setCorrection} rows={3} maxLength={8000} />
          <button type="button" className="btn btn-sm btn-primary" style={{ width: 'max-content' }} disabled={busy || !correction.trim()} onClick={() => void send('not-useful', correction)}>
            {t('mem.fb.sendCorrection')}
          </button>
        </div>
      )}
      {error ? <ProblemBox error={error} /> : null}
    </div>
  );
}
